// redux/slices/paymentsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getPaymentSettingsApi,
  getOwnerPaymentSettingsApi,
  updatePaymentSettingsApi,
  createPaymentApi,
  getPaymentsApi,
  getPaymentByIdApi,
  updatePaymentStatusApi,
  linkPaymentToOrderApi,
} from '../../api/paymentApi';

// ========== ASYNC THUNKS ==========

// Get payment settings
export const getPaymentSettings = createAsyncThunk(
  'payments/getSettings',
  async (restaurantId, { rejectWithValue }) => {
    try {
      const response = await getPaymentSettingsApi(restaurantId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get payment settings
export const getOwnerPaymentSettings = createAsyncThunk(
  'payments/getOwnerSettings',
  async (__, { rejectWithValue }) => {
    try {
      const response = await getOwnerPaymentSettingsApi();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update payment settings
export const updatePaymentSettings = createAsyncThunk(
  'payments/updateSettings',
  async (settingsData, { rejectWithValue }) => {
    try {
      const response = await updatePaymentSettingsApi(settingsData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Create payment
export const createPayment = createAsyncThunk(
  'payments/create',
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await createPaymentApi(paymentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get all payments
export const getPayments = createAsyncThunk(
  'payments/getAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getPaymentsApi(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get payment by ID
export const getPaymentById = createAsyncThunk(
  'payments/getById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getPaymentByIdApi(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update payment status
export const updatePaymentStatus = createAsyncThunk(
  'payments/updateStatus',
  async ({ id, statusData }, { rejectWithValue }) => {
    try {
      const response = await updatePaymentStatusApi(id, statusData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// 🔥 NEW: Link payment to order
export const linkPaymentToOrder = createAsyncThunk(
  'payments/linkToOrder',
  async ({ paymentId, orderId }, { rejectWithValue }) => {
    try {
      const response = await linkPaymentToOrderApi({ paymentId, orderId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ========== INITIAL STATE ==========

const initialState = {
  // Settings
  settings: {
    cashEnabled: true,
    upiEnabled: true,
    upiId: '',
    upiQrCode: '',
    phonePeEnabled: true,
    phonePeNumber: '',
  },
  settingsLoading: false,
  settingsError: null,
  settingsSuccess: false,

  // Payments List
  payments: [],
  paymentsLoading: false,
  paymentsError: null,
  paymentsPagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },

  // Single Payment
  currentPayment: null,
  paymentLoading: false,
  paymentError: null,

  // Create/Update Status
  operationLoading: false,
  operationSuccess: false,
  operationError: null,
};

// ========== SLICE ==========

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    // Clear states
    clearPaymentError: (state) => {
      state.paymentsError = null;
      state.settingsError = null;
      state.operationError = null;
    },
    
    clearOperationState: (state) => {
      state.operationLoading = false;
      state.operationSuccess = false;
      state.operationError = null;
    },

    clearCurrentPayment: (state) => {
      state.currentPayment = null;
    },

    // Update local settings (for form)
    updateLocalSettings: (state, action) => {
      state.settings = { ...state.settings, ...action.payload };
    },

    // Reset settings to default
    resetSettings: (state) => {
      state.settings = {
        cashEnabled: true,
        upiEnabled: true,
        upiId: '',
        upiQrCode: '',
        phonePeEnabled: true,
        phonePeNumber: '',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== GET SETTINGS =====
      .addCase(getPaymentSettings.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
      })
      .addCase(getPaymentSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings = action.payload.data || action.payload;
        state.settingsError = null;
      })
      .addCase(getPaymentSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.payload?.message || 'Failed to load settings';
      })

       // ===== GET OWNER SETTINGS =====
      .addCase(getOwnerPaymentSettings.pending, (state) => {
        state.settingsLoading = true;
        state.settingsError = null;
      })
      .addCase(getOwnerPaymentSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings = action.payload.data || action.payload;
        state.settingsError = null;
      })
      .addCase(getOwnerPaymentSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.settingsError = action.payload?.message || 'Failed to load settings';
      })

      // ===== UPDATE SETTINGS =====
      .addCase(updatePaymentSettings.pending, (state) => {
        state.operationLoading = true;
        state.operationSuccess = false;
        state.operationError = null;
      })
      .addCase(updatePaymentSettings.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.settings = action.payload.data || action.payload;
        state.operationError = null;
      })
      .addCase(updatePaymentSettings.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError = action.payload?.message || 'Failed to update settings';
      })

      // ===== GET ALL PAYMENTS =====
      .addCase(getPayments.pending, (state) => {
        state.paymentsLoading = true;
        state.paymentsError = null;
      })
      .addCase(getPayments.fulfilled, (state, action) => {
        state.paymentsLoading = false;
        state.payments = action.payload.data || [];
        state.paymentsPagination = action.payload.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          pages: 0,
        };
        state.paymentsError = null;
      })
      .addCase(getPayments.rejected, (state, action) => {
        state.paymentsLoading = false;
        state.paymentsError = action.payload?.message || 'Failed to load payments';
      })

      // ===== GET PAYMENT BY ID =====
      .addCase(getPaymentById.pending, (state) => {
        state.paymentLoading = true;
        state.paymentError = null;
      })
      .addCase(getPaymentById.fulfilled, (state, action) => {
        state.paymentLoading = false;
        state.currentPayment = action.payload.data || action.payload;
        state.paymentError = null;
      })
      .addCase(getPaymentById.rejected, (state, action) => {
        state.paymentLoading = false;
        state.paymentError = action.payload?.message || 'Failed to load payment';
      })

      // ===== CREATE PAYMENT =====
      .addCase(createPayment.pending, (state) => {
        state.operationLoading = true;
        state.operationSuccess = false;
        state.operationError = null;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        // Add new payment to list if exists
        if (state.payments) {
          state.payments.unshift(action.payload.data || action.payload);
        }
        state.operationError = null;
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError = action.payload?.message || 'Failed to create payment';
      })

      // ===== UPDATE PAYMENT STATUS =====
      .addCase(updatePaymentStatus.pending, (state) => {
        state.operationLoading = true;
        state.operationSuccess = false;
        state.operationError = null;
      })
      .addCase(updatePaymentStatus.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        
        // Update in payments list
        const updatedPayment = action.payload.data || action.payload;
        if (state.payments) {
          const index = state.payments.findIndex(p => p._id === updatedPayment._id);
          if (index !== -1) {
            state.payments[index] = updatedPayment;
          }
        }
        
        // Update current payment if it's the same
        if (state.currentPayment?._id === updatedPayment._id) {
          state.currentPayment = updatedPayment;
        }
        
        state.operationError = null;
      })
      .addCase(updatePaymentStatus.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError = action.payload?.message || 'Failed to update payment status';
      })

      
.addCase(linkPaymentToOrder.pending, (state) => {
  state.operationLoading = true;
  state.operationError = null;
})
.addCase(linkPaymentToOrder.fulfilled, (state, action) => {
  state.operationLoading = false;
  state.operationSuccess = true;
  
  // Update payment in list if exists
  const updatedPayment = action.payload.data || action.payload;
  if (state.payments) {
    const index = state.payments.findIndex(p => p._id === updatedPayment._id);
    if (index !== -1) {
      state.payments[index] = updatedPayment;
    }
  }
  
  // Update current payment if it's the same
  if (state.currentPayment?._id === updatedPayment._id) {
    state.currentPayment = updatedPayment;
  }
  
  state.operationError = null;
})
.addCase(linkPaymentToOrder.rejected, (state, action) => {
  state.operationLoading = false;
  state.operationError = action.payload?.message || 'Failed to link payment';
})
      
  },
});

// ========== ACTIONS ==========
export const {
  clearPaymentError,
  clearOperationState,
  clearCurrentPayment,
  updateLocalSettings,
  resetSettings,
} = paymentsSlice.actions;

// ========== SELECTORS ==========
export const selectPaymentSettings = (state) => state.payments.settings;
export const selectSettingsLoading = (state) => state.payments.settingsLoading;
export const selectSettingsError = (state) => state.payments.settingsError;

export const selectPayments = (state) => state.payments.payments;
export const selectPaymentsLoading = (state) => state.payments.paymentsLoading;
export const selectPaymentsError = (state) => state.payments.paymentsError;
export const selectPaymentsPagination = (state) => state.payments.paymentsPagination;

export const selectCurrentPayment = (state) => state.payments.currentPayment;
export const selectPaymentLoading = (state) => state.payments.paymentLoading;
export const selectPaymentError = (state) => state.payments.paymentError;

export const selectOperationLoading = (state) => state.payments.operationLoading;
export const selectOperationSuccess = (state) => state.payments.operationSuccess;
export const selectOperationError = (state) => state.payments.operationError;

// Summary selectors
export const selectTotalPayments = (state) => state.payments.paymentsPagination.total;
export const selectTodayPayments = (state) => {
  const payments = state.payments.payments || [];
  const today = new Date().toDateString();
  return payments.filter(p => 
    p.paidAt && new Date(p.paidAt).toDateString() === today
  );
};

export default paymentsSlice.reducer;